import { Box, Chip, Tooltip, Typography } from "@mui/material";
import React from "react";
import dayjs from "dayjs";
import { Voucher } from "../../model/merchant_model";
import {
  TableCell,
  TableEmptyState,
  TableFooterPagination,
  TableHeaderRow,
  TableRow,
} from "../../../users/components/table_toolkit/table_toolkit";
import { black, greyEnabled, purple, purpleLight } from "../../../../theme/colors";

interface VouchersTableInterface {
  data: Voucher[];
  count: number;
  page: number;
  setPage: (val: number) => void;
  pageSize: number;
  setPageSize: (val: number) => void;
}

const ROWS_PER_PAGE_OPTIONS = [8, 25, 50, 100];

function statusColors(status: string | null) {
  switch (status?.toLowerCase()) {
    case "redeemed":
      return { bgcolor: "#E7F5EC", color: "#1E6446" };
    case "unassigned":
      return { bgcolor: "#FAEDEA", color: "#D14C2E" };
    case "assigned":
    case "assignedtouser":
      return { bgcolor: purpleLight, color: purple };
    case "redemption_in_progress":
      return { bgcolor: "#E3F2FD", color: "#1565C0" };
    case "expired":
      return { bgcolor: "#FBD9D6", color: "#C0392B" };
    default:
      return { bgcolor: "#F1E9F1", color: greyEnabled };
  }
}

export default function VouchersTable({
  data,
  page,
  setPage,
  pageSize,
  setPageSize,
  count,
}: VouchersTableInterface) {
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const startRange = page * pageSize + 1;
  const endRange = Math.min((page + 1) * pageSize, count);
  const customText = `Showing ${count ? startRange : 0} to ${endRange || 0} of ${count || 0} entries`;
  const showList = data.length > 0;

  return (
    <Box px={{ xs: 2.5, sm: 3 }} pb={2}>
      {!showList ? (
        <TableEmptyState message="No vouchers found" hint="Try adjusting your search or filters." />
      ) : (
        <Box mt={1}>
          <TableHeaderRow
            labels={[
              { label: "Sl. No.", flex: 0.3 },
              { label: "Voucher", flex: 1.1 },
              { label: "Amount", flex: 0.6 },
              { label: "Status", flex: 0.8 },
              { label: "Merchant", flex: 1.2 },
              { label: "User", flex: 1.2 },
              { label: "Created", flex: 0.9 },
              { label: "Redeemed" },
            ]}
            align="left"
            bordered
          />
          {data.map((voucher, index) => (
            <TableRow key={voucher.id ?? index} bordered>
              <TableCell label="Sl. No." flex={0.3} bordered>
                <Typography fontSize={14} color={black} noWrap>
                  {page * pageSize + index + 1}
                </Typography>
              </TableCell>
              <TableCell label="Voucher" flex={1.1} bordered>
                <Tooltip title={voucher.voucher_code || ""}>
                  <Typography fontSize={14} color={black} noWrap sx={{ cursor: "help" }}>
                    {voucher.voucher_code || "-"}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell label="Amount" flex={0.6} bordered>
                <Typography fontSize={14} fontWeight={600} color={black} noWrap>
                  ₹{voucher.amount}
                </Typography>
              </TableCell>
              <TableCell label="Status" flex={0.8} bordered>
                <Chip
                  label={voucher.status || "N/A"}
                  size="small"
                  sx={{ ...statusColors(voucher.status), fontWeight: 600, height: 20 }}
                />
              </TableCell>
              <TableCell label="Merchant" flex={1.2} bordered>
                <Typography fontSize={14} color={black} noWrap>
                  {voucher.merchant_name || "-"}
                </Typography>
                {voucher.merchant_id && (
                  <Typography variant="body2" color={greyEnabled} noWrap>
                    {voucher.merchant_id}
                  </Typography>
                )}
              </TableCell>
              <TableCell label="User" flex={1.2} bordered>
                <Typography fontSize={14} color={black} noWrap>
                  {voucher.user_mobile || "-"}
                </Typography>
                {voucher.user_id && (
                  <Typography variant="body2" color={greyEnabled} noWrap>
                    {voucher.user_id}
                  </Typography>
                )}
              </TableCell>
              <TableCell label="Created" flex={0.9} bordered>
                <Typography fontSize={14} color={black} noWrap>
                  {voucher.createdAt ? dayjs(voucher.createdAt).format("DD-MM-YYYY") : "-"}
                </Typography>
                <Typography variant="body2" color={greyEnabled} noWrap>
                  {voucher.createdAt ? dayjs(voucher.createdAt).format("hh:mm A") : ""}
                </Typography>
              </TableCell>
              <TableCell isLast bordered>
                <Typography fontSize={14} color={black} noWrap>
                  {voucher.redeemedAt ? dayjs(voucher.redeemedAt).format("DD-MM-YYYY") : "-"}
                </Typography>
                {voucher.redeemedAt && (
                  <Typography variant="body2" color={greyEnabled} noWrap>
                    {dayjs(voucher.redeemedAt).format("hh:mm A")}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </Box>
      )}

      {showList && (
        <Box mt={2}>
          <TableFooterPagination
            count={count}
            page={page}
            onPageChange={handleChangePage}
            summaryText={customText}
            rowsPerPage={pageSize}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            onRowsPerPageChange={setPageSize}
          />
        </Box>
      )}
    </Box>
  );
}
